import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BulkActionModel, {
  type BulkActionCustomDeleteContacts,
  type BulkActionResponseDeleteContacts,
} from 'donor-elf-web/models/bulk-action';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import type { BulkActionsContactsChangedArgs } from '.';

interface ComponentArgs {
  contactIds: string[];
  onContactsChanged: (args: BulkActionsContactsChangedArgs) => void;
  onClose: () => void;
}

export default class ContactBulkActionsDeleteContactsComponent extends Component<ComponentArgs> {
  @service declare contactsCache: ContactsCache;
  @service declare store: Store;

  bulkAction?: BulkActionModel;
  @tracked waitingOnBulkActionToComplete = false;
  @tracked saveError = false;

  @tracked bulkActionComplete = false;
  @tracked someContactsWereHidden = false;

  get showFooter() {
    return !this.waitingOnBulkActionToComplete;
  }

  @action
  async delete() {
    this.waitingOnBulkActionToComplete = true;
    this.bulkAction = this.store.createRecord('bulk-action');
    this.bulkAction.actionType = 'delete_contacts';
    (this.bulkAction.custom as BulkActionCustomDeleteContacts) = {
      contact_ids: this.args.contactIds,
    };
    try {
      await this.bulkAction.save();
    } catch (error: any) {
      this.saveError = true;
    }
  }

  @action
  async handleBulkActionStatusChange(bulkAction: BulkActionModel) {
    if (bulkAction.status === 'completed') {
      const response = bulkAction.response as BulkActionResponseDeleteContacts;
      await bulkAction.destroyRecord();

      if (response.contact_ids_deleted?.length > 0) {
        this.contactsCache.contacts = this.contactsCache.contacts.filter(
          (x) => !response.contact_ids_deleted.some((y) => x.id == y),
        );
      }
      if (response.contact_ids_hidden?.length > 0) {
        if (response.contact_ids_hidden.length < 250) {
          await this.contactsCache.reloadSpecificContacts(
            response.contact_ids_hidden,
          );
        } else {
          await this.contactsCache.loadContacts({ forceLoad: true });
        }
      }
      this.args.onContactsChanged({
        contactIds: response.contact_ids_hidden,
        contactIdsDeleted: response.contact_ids_deleted,
        bulkActionType: 'delete_contacts',
      });
      this.someContactsWereHidden = response.contact_ids_hidden.length > 0;
      this.waitingOnBulkActionToComplete = false;
      this.bulkActionComplete = true;
      if (!this.someContactsWereHidden) {
        this.args.onClose();
      }
    } else if (bulkAction.status === 'error') {
      this.saveError = true;
    }
  }
}
